import React, { useState, useContext, useMemo } from 'react';
import { 
  Flex, 
  IconButton, 
  Text, 
  HStack, 
  Tooltip, 
  Box,
  useBreakpointValue
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  List, 
  X,
  Home,
  Volume2,
  VolumeX,
  Info,
  Play,
  Pause,
  SkipForward,
  Eye,
  MessageSquare,
  MessageSquareOff,
  RefreshCw
} from 'lucide-react';
import SlideInfo from './SlideInfo';
import { useAudio } from '../context/AudioContext';

// Create a context for video controls
export const VideoControlContext = React.createContext<{
  isVideoPlaying: boolean;
  toggleVideoPlayback?: () => void;
  skipVideo?: () => void;
  videoProgress: number;
  isVideoSlide: boolean;
}>({ 
  isVideoPlaying: false, 
  videoProgress: 0,
  isVideoSlide: false
});

interface SlideControlsProps {
  currentSlide: number;
  totalSlides: number;
  onNext?: () => void;
  onPrevious?: () => void;
  onNextSlide?: () => void;
  onPrevSlide?: () => void;
  onHome?: () => void;
  onToggleSpeech?: () => void;
  onToggleCaptions?: () => void;
  onRestartAudio?: () => void;
  isSpeaking?: boolean;
  showCaptions?: boolean;
  onSlideSelect?: (index: number) => void;
  slideId?: string;
  disablePrevious?: boolean;
  onShowUISample?: () => void;
  isWelcomeScreen?: boolean;
  isMuted?: boolean;
  toggleMute?: () => void;
  slides?: any[];
  onHelp?: () => void;
}

const MotionFlex = motion(Flex);
const MotionBox = motion(Box);

const SlideControls: React.FC<SlideControlsProps> = ({
  currentSlide,
  totalSlides,
  onNext,
  onPrevious,
  onNextSlide,
  onPrevSlide,
  onHome,
  onToggleSpeech,
  onToggleCaptions,
  onRestartAudio,
  isSpeaking = false,
  showCaptions = false,
  onSlideSelect,
  slideId = '',
  disablePrevious = false,
  onShowUISample,
  isWelcomeScreen = false,
  isMuted,
  toggleMute,
  slides,
  onHelp,
}) => {
  const [showIndex, setShowIndex] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const isCompact = useBreakpointValue({ base: true, md: false });
  
  // Access audio context
  const { isMuted: audioIsMuted, toggleMute: audioToggleMute } = useAudio();
  
  // Access video control context
  const { isVideoPlaying, toggleVideoPlayback, skipVideo, videoProgress, isVideoSlide } = useContext(VideoControlContext);
  
  // Use the appropriate handler (support both naming conventions)
  const handleNext = onNext || onNextSlide;
  const handlePrevious = onPrevious || onPrevSlide;
  
  // Special handling for welcome screen navigation
  const handleNextWithWelcomeCheck = () => {
    console.log('Next button clicked, isWelcomeScreen:', isWelcomeScreen);
    if (isWelcomeScreen) {
      // If we're on the welcome screen, we need to ensure we call handleNext
      // which will call handleStart in WelcomeStartScreen
      console.log('On welcome screen, calling handleNext to transition to Presenter Bio');
      // When on welcome screen, directly call the next handler to start the presentation
      if (handleNext) {
        handleNext();
        return;
      }
    } else if (handleNext) {
      handleNext();
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' && handleNext) {
      handleNextWithWelcomeCheck();
    } else if (e.key === 'ArrowLeft' && handlePrevious && !disablePrevious) {
      handlePrevious();
    }
  };
  
  // Check if the current slide has a UI sample
  const currentSlideHasUISample = useMemo(() => {
    // Check if window.slides exists
    if (typeof window === 'undefined' || !(window as any).slides) {
      return false;
    }
    
    // Check if current slide exists
    const slides = (window as any).slides;
    if (!slides[currentSlide]) {
      return false;
    }
    
    // Check if current slide has details and uiSample
    const slide = slides[currentSlide];
    return slide.details && slide.details.uiSample;
  }, [currentSlide]);

  // Only show UI Sample button if the current slide has a UI sample
  const showUISampleButton = currentSlideHasUISample && onShowUISample;
  
  return (
    <Flex 
      position="fixed" 
      bottom="20px" 
      left="0" 
      right="0" 
      justifyContent="center" 
      alignItems="center"
      zIndex={100}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <MotionFlex
        bg="rgba(0, 0, 0, 0.7)"
        backdropFilter="blur(10px)"
        borderRadius="full"
        p={2}
        width="500px"
        justifyContent="center"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
        border="1px solid rgba(255, 255, 255, 0.1)"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <HStack spacing={2}>
          {/* Home button */}
          {onHome && (
            <Tooltip label="Go to home" placement="top">
              <IconButton
                aria-label="Go to home"
                icon={<Home size={18} />}
                variant="ghost"
                colorScheme="blue"
                size="sm"
                onClick={onHome}
                _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
              />
            </Tooltip>
          )}
          
          {/* Previous button */}
          <Tooltip label="Previous slide" placement="top">
            <IconButton
              aria-label="Previous slide"
              icon={<ChevronLeft size={20} />}
              variant="ghost"
              colorScheme="blue"
              size="sm"
              onClick={handlePrevious}
              isDisabled={currentSlide <= 0 || disablePrevious}
              _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            />
          </Tooltip>
          
          {/* Slide indicator */}
          <Tooltip label={showIndex ? "Hide slide index" : "Show slide index"} placement="top">
            <Box
              px={3}
              py={1}
              borderRadius="full"
              bg="rgba(255, 255, 255, 0.1)"
              cursor="pointer"
              onClick={() => setShowIndex(!showIndex)}
              minWidth="160px"
              textAlign="center"
            >
              <Text color="white" fontSize="sm">
                {isWelcomeScreen ? "Welcome" :
                 currentSlide === 0 ? "Welcome" :
                 currentSlide === 1 ? "Bio" :
                 currentSlide === 2 ? "Platform Overview" :
                 currentSlide === 3 ? "Resilience" :
                 currentSlide === 4 ? "Culture" :
                 currentSlide === 5 ? "Efficiency" :
                 currentSlide === 6 ? "AI Companion" :
                 currentSlide === 7 ? "User Journey" :
                 currentSlide === 8 ? "JIMI Video" :
                 currentSlide === 9 ? "Crowdsourcing" :
                 currentSlide === 10 ? "Governance" :
                 currentSlide === 11 ? "Implementation" :
                 currentSlide === 12 ? "Timeline" :
                 currentSlide === 13 ? "Budget" :
                 currentSlide === 14 ? "ROI" :
                 currentSlide === 15 ? "Adoption" :
                 currentSlide === 16 ? "Cost-Benefit" :
                 currentSlide === 17 ? "Next Steps" :
                 currentSlide === 18 ? "Q&A" :
                 currentSlide === 19 ? "Thank You" :
                 `${currentSlide + 1} / ${totalSlides}`}
              </Text>
            </Box>
          </Tooltip>
          
          {/* Next button */}
          <Tooltip label="Next slide" placement="top">
            <IconButton
              aria-label="Next slide"
              icon={<ChevronRight size={20} />}
              variant="ghost"
              colorScheme="blue"
              size="sm"
              onClick={handleNextWithWelcomeCheck}
              isDisabled={currentSlide >= totalSlides - 1}
              _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            />
          </Tooltip>

          {/* Audio controls */}
          <Tooltip label={isSpeaking ? "Pause narration" : "Play narration"} placement="top">
            <IconButton
              aria-label={isSpeaking ? "Pause narration" : "Play narration"}
              icon={isSpeaking ? <Pause size={18} /> : <Play size={18} />}
              variant="ghost"
              colorScheme="blue"
              size="sm"
              onClick={onToggleSpeech}
              _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            />
          </Tooltip>

          {/* Mute/Unmute button */}
          <Tooltip label={isMuted ? "Unmute" : "Mute"} placement="top">
            <IconButton
              aria-label={isMuted ? "Unmute" : "Mute"}
              icon={isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              variant="ghost"
              colorScheme="blue"
              size="sm"
              onClick={toggleMute}
              _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            />
          </Tooltip>
        </HStack>
      </MotionFlex>
      
      {/* Slide index panel */}
      <AnimatePresence>
        {showIndex && onSlideSelect && (
          <MotionBox
            position="fixed"
            bottom="80px"
            left="50%"
            transform="translateX(-50%)"
            bg="rgba(0, 0, 0, 0.8)"
            backdropFilter="blur(10px)"
            borderRadius="xl"
            p={4}
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.4)"
            border="1px solid rgba(255, 255, 255, 0.1)"
            maxHeight="300px"
            overflowY="auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            zIndex={1000}
          >
            <Flex direction="column" gap={2}>
              {/* Existing slide options */}
              {Array.from({ length: totalSlides }).map((_, index) => (
                <Flex
                  key={index}
                  py={2}
                  px={4}
                  borderRadius="md"
                  bg={currentSlide === index ? "blue.500" : "transparent"}
                  color="white"
                  cursor="pointer"
                  onClick={() => {
                    console.log(`Selecting slide index: ${index}`);
                    onSlideSelect(index);
                    setShowIndex(false);
                  }}
                  _hover={{
                    bg: currentSlide === index ? "blue.600" : "rgba(255, 255, 255, 0.1)"
                  }}
                >
                  <Text fontSize="sm">
                    {index === 0 ? "Welcome" :
                     index === 1 ? "Bio" :
                     index === 2 ? "Platform Overview" :
                     index === 3 ? "Resilience" :
                     index === 4 ? "Culture" :
                     index === 5 ? "Efficiency" :
                     index === 6 ? "AI Companion" :
                     index === 7 ? "User Journey" :
                     index === 8 ? "JIMI Video" :
                     index === 9 ? "Crowdsourcing" :
                     index === 10 ? "Governance" :
                     index === 11 ? "Implementation" :
                     index === 12 ? "Timeline" :
                     index === 13 ? "Budget" :
                     index === 14 ? "ROI" :
                     index === 15 ? "Adoption" :
                     index === 16 ? "Cost-Benefit" :
                     index === 17 ? "Next Steps" :
                     index === 18 ? "Q&A" :
                     index === 19 ? "Thank You" :
                     `Slide ${index + 1}`}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </MotionBox>
        )}
      </AnimatePresence>
    </Flex>
  );
};

export default SlideControls;
